<template>
  <ResponsiveLayout>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <CorporateHeader />
      <div class="kds-content-block">
        <p
          class="kds-typography-display-small"
          v-html="$t('b2b_voucher_welcome_text', { name: corporateName })"
        />
        <p
          class="kds-typography-paragraph-large"
          v-html="$t('b2b_voucher_instructions_intro')"
        />
      </div>
      <div class="kds-content-block">
        <div>
          <h4
            class="kds-typography-title-medium"
            v-html="$t('b2b_voucher_instructions_step_1_title')"
          />
          <p
            class="kds-typography-ui-subtle-large"
            v-html="$t('b2b_voucher_instructions_step_1_text')"
          />
        </div>
        <div>
          <h4
            class="kds-typography-title-medium"
            v-html="$t('b2b_voucher_instructions_step_2_title')"
          />
          <p
            class="kds-typography-ui-subtle-large"
            v-html="$t('b2b_voucher_instructions_step_2_text')"
          />
        </div>
        <div>
          <h4
            class="kds-typography-title-medium"
            v-html="$t('b2b_voucher_instructions_step_3_title')"
          />
          <p
            class="kds-typography-ui-subtle-large"
            v-html="$t('b2b_voucher_instructions_step_3_text')"
          />
        </div>
      </div>
      <form id="voucher_form">
        <TextField
          v-bind="fieldAttrs.voucherCode"
          :label="$t('b2b_voucher_placeholder')"
          data-qa="voucher-code"
        />
      </form>
    </div>
    <template #actions>
      <BaseButton
        v-bind="submitAttrs"
        :text="$t('b2b_voucher_submit')"
        data-qa="verify-voucher"
        form="voucher_form"
      />
    </template>
    <template #footer><FooterLinks v-if="!isFromApp()" /></template>
  </ResponsiveLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useUsStore from '@us/app/stores/useUsStore.js'
import { useI18n } from 'petite-vue-i18n'

import useForm from '@shared/composables/useForm.js'
import ROUTE from '@us/app/router/appModule/names'

import TextField from '@shared/components/form/TextField.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import CorporateHeader from '@shared/components/CorporateHeader.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import FooterLinks from '@us/app/components/FooterLinks.vue'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import HeaderHelp from '@us/app/components/HeaderHelp.vue'
import useSharedStore from '@shared/stores/useSharedStore.js'

const { t } = useI18n()
const corporateStore = useCorporateStore()
const usStore = useUsStore()
const sharedStore = useSharedStore()
const route = useRoute()
const router = useRouter()

const corporateName = computed(() => corporateStore.corporate.title || '')

const form = ref({
  voucherCode: usStore.formData.voucherCode || '',
})

const formConfig = computed(() => ({
  voucherCode: {
    validations: ['required'],
  },
}))

const { fieldAttrs, submitAttrs, submit, addErrors } = useForm(
  form,
  formConfig,
  {
    onSubmit: async () => {
      await usStore.checkVoucher(form.value.voucherCode.trim())
    },
    onSuccess: () => {
      router.push({
        name: ROUTE.SIGN_UP,
        params: {
          // redirect to the corporate identified during the eligibility check
          corporate_key: usStore.corporateEligibilityData.corporate_key,
        },
      })
    },
    onError: (error) => {
      if (error.response && error.response.status === 403) {
        addErrors('voucherCode', t('generic_voucher_not_valid'))
      }
    },
  },
)

onMounted(() => {
  if (route.query.retry) return
  form.value.voucherCode =
    route.params?.voucherCode || sharedStore.initialQuery.voucher || ''

  if (form.value.voucherCode) {
    submit()
  }
})
</script>

<style lang="scss"></style>
